import { useCallback, useEffect } from 'react';
import { useCustomerUpdatesPageStyles } from './CustomerUpdatesPage.styles';
import { ISorting } from '../../model';
import { app } from '@microsoft/teams-js';
import CustomerUpdatesTable from './CustomerUpdatesTable/CustomerUpdatesTable';
import { useParams, useSearchParams } from 'react-router';
import { useAppDispatch, useSelector } from '../../store/hooks';
import {
    customerUpdatesState,
    toggleSort,
    CustomerUpdatesFiltersState,
    updateFilters,
    setQueryText,
    setCurrentPage,
    setFlightTeamId,
    CustomerUpdatesViewType,
    setViewType,
} from '../../store/features/customerUpdates/customerUpdatesSlice';
import CustomerUpdatesToolbar from './CustomerUpdatesToolbar/CustomerUpdatesToolbar';
import { useDebouncedCallback } from 'use-debounce';
import BaggageInfoDialog from './BaggageInfoDialog/BaggageInfoDialog';
import PaginationBar from '../PaginationBar';
import { Spinner } from '@fluentui/react-components';
import { loadConfig } from '../../store/features/config/configSlice';
import MisconnectedCustomerInfoDialog from './MisconnectedCustomersInfoDialog/MisconnectedCustomerInfoDialog';
import {
    loadUnableToNotifyReasons,
    markCustomerNotificationAction,
    loadCustomerUpdates,
    loadCustomerUpdatesFilters,
} from '../../store/features/customerUpdates/customerUpdates.thunks';
import { CustomerWithStatuses } from '../../model/customerWithUpdates';
import {
    CustomerUpdatesType,
    ICustomerNotificationActionContext,
} from '../../store/features/customerUpdates/customerUpdates.types';
import { useValueDialog } from '../../hooks/useDialog';
import CustomerMessageDialog from './CustomerMessageDialog/CustomerMessageDialog';

interface FlightBaggageStatusPageProps {
    subEntityId?: string;
}

export const customerUpdatesPageParamKeys = {
    viewType: 'viewType',
} as const;

const CustomerUpdatesPage = ({ subEntityId }: FlightBaggageStatusPageProps) => {
    const styles = useCustomerUpdatesPageStyles();

    const dispatch = useAppDispatch();
    const { flightTeamId: flightTeamIdParam } = useParams();
    const flightTeamId = subEntityId ?? flightTeamIdParam;

    const [searchParams] = useSearchParams();
    const viewType =
        (searchParams.get(
            customerUpdatesPageParamKeys.viewType
        ) as CustomerUpdatesViewType) || 'All';

    const { customers, sorting, pagination, loadStatus } =
        useSelector(customerUpdatesState);

    const reloadCustomerUpdates = useCallback(() => {
        dispatch(loadCustomerUpdates());
    }, [dispatch]);

    useEffect(() => {
        app.notifySuccess();
        if (flightTeamId) {
            dispatch(toggleSort('seatNumber')); // default sorting when flightTeamId is provided (tab is opened in flight team)
            dispatch(loadUnableToNotifyReasons());
        }
        dispatch(setFlightTeamId(flightTeamId));
        dispatch(setViewType(viewType));
        dispatch(loadConfig());

        dispatch(loadCustomerUpdatesFilters()).then(() =>
            reloadCustomerUpdates()
        );
    }, [reloadCustomerUpdates, dispatch, flightTeamId, viewType]);

    const onFilterUpdateCallback = useCallback(
        (filter: Partial<CustomerUpdatesFiltersState>) => {
            dispatch(updateFilters(filter));
            reloadCustomerUpdates();
        },
        [dispatch, reloadCustomerUpdates]
    );

    const onSearchQueryUpdateCallback = useDebouncedCallback(
        (query: string) => {
            dispatch(setQueryText(query));
            reloadCustomerUpdates();
        },
        300
    );

    const onSortingChangeCallback = useCallback(
        (sorting: ISorting) => {
            dispatch(toggleSort(sorting.sortBy));
            reloadCustomerUpdates();
        },
        [dispatch, reloadCustomerUpdates]
    );

    const onPaginationChangeCallback = useCallback(
        (page: number) => {
            onSearchQueryUpdateCallback.cancel();
            dispatch(setCurrentPage(page));
            reloadCustomerUpdates();
        },
        [onSearchQueryUpdateCallback, dispatch, reloadCustomerUpdates]
    );

    const dialogs = {
        misconnectedCustomers: useValueDialog<CustomerWithStatuses>(),
        mishandledBaggage: useValueDialog<CustomerWithStatuses>(),
        customerMessages: useValueDialog<CustomerWithStatuses>(),
    };

    const onPassengerSelectCallback = useCallback(
        (customerInfo: CustomerWithStatuses, type: CustomerUpdatesType) => {
            switch (type) {
                case 'MishandledBaggage':
                    dialogs.mishandledBaggage.openDialog(customerInfo);
                    break;
                case 'MisconnectedCustomers':
                    dialogs.misconnectedCustomers.openDialog(customerInfo);
                    break;
                case 'CustomerMessages':
                    dialogs.customerMessages.openDialog(customerInfo);
                    break;
            }
        },
        [
            dialogs.misconnectedCustomers,
            dialogs.mishandledBaggage,
            dialogs.customerMessages,
        ]
    );

    const onDialogResultCallback = useCallback(
        (context: ICustomerNotificationActionContext) => {
            dispatch(markCustomerNotificationAction({ ...context })).then(() =>
                dispatch(loadCustomerUpdates())
            );
        },
        [dispatch]
    );

    const isFlightTeamBoard = !!flightTeamId;

    return (
        <div className={styles.root}>
            <CustomerUpdatesToolbar
                onFilterUpdate={onFilterUpdateCallback}
                onSearchQueryUpdate={onSearchQueryUpdateCallback}
            />
            <CustomerUpdatesTable
                isFlightTeamBoard={isFlightTeamBoard}
                items={customers}
                sorting={sorting}
                onSortingChange={onSortingChangeCallback}
                onPassengerSelect={onPassengerSelectCallback}
            />
            <PaginationBar
                currentPage={pagination.currentPage}
                pageSize={pagination.pageSize}
                totalItems={pagination.totalCount}
                onChange={onPaginationChangeCallback}
            />
            <BaggageInfoDialog
                open={dialogs.mishandledBaggage.isOpen}
                customerInfo={dialogs.mishandledBaggage.value}
                isFlightTeamBoard={isFlightTeamBoard}
                onResultClick={onDialogResultCallback}
                onOpenChange={dialogs.mishandledBaggage.onOpenChangeCallback}
            />
            <MisconnectedCustomerInfoDialog
                open={dialogs.misconnectedCustomers.isOpen}
                customerInfo={dialogs.misconnectedCustomers.value}
                isFlightTeamBoard={isFlightTeamBoard}
                onResultClick={onDialogResultCallback}
                onOpenChange={
                    dialogs.misconnectedCustomers.onOpenChangeCallback
                }
            />
            <CustomerMessageDialog
                open={dialogs.customerMessages.isOpen}
                customerInfo={dialogs.customerMessages.value}
                isFlightTeamBoard={isFlightTeamBoard}
                onResultClick={onDialogResultCallback}
                onOpenChange={dialogs.customerMessages.onOpenChangeCallback}
            />
            {loadStatus === 'loading' && (
                <Spinner className={styles.spinner} appearance="primary" />
            )}
        </div>
    );
};

export default CustomerUpdatesPage;
